import { isDefined } from "@clipboard-health/util-ts";
import { Box, type Theme } from "@mui/material";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { showErrorToast } from "@src/appV2/lib/Notifications";
import { NoShowWarningContent } from "@src/appV2/redesign/Shift/components/NoShowWarningContent";
import { useNoShowWarning } from "@src/appV2/redesign/Shift/hooks/useNoShowWarning";
import { type UseModalState } from "@src/lib/ui-react";
import { useEffect, useState } from "react";

import { useIsMobile } from "../../AppLayout/useResponsive";
import { BottomSheet } from "../../components/BottomSheet";
import { Dialog } from "../../components/Dialog";
import { DialogFooter } from "../../components/DialogFooter";
import { DialogHeader } from "../../components/DialogHeader";
import { useReportNoShow } from "../../DailyView/api/useReportNoShow";
import { GeofenceWarningBanner } from "../../DailyView/components/ReportIssueDialog/GeofenceWarningBanner";
import { NoShowFormContent } from "../../Shift/components/NoShowFormContent";

interface FcmNoShowDialogProps {
  modalState: UseModalState;
  shiftId: string;
  workerId: string;
  workplaceId: string;
  shiftStart?: string;
  canRequestReplacement?: boolean;
  geofenceEntryTime?: string;
  timezone?: string;
  onSuccess: () => void;
  onCancel: () => void;
}

/**
 * Modal for reporting a No Show after rejecting an FCM (Facility Canceled Me / Sent Home) request.
 * Shown when the user selects "This was a No Show" during FCM rejection.
 */
export function FcmNoShowDialog(props: FcmNoShowDialogProps) {
  const {
    modalState,
    shiftId,
    workerId,
    workplaceId,
    shiftStart,
    canRequestReplacement,
    geofenceEntryTime,
    timezone,
    onSuccess,
    onCancel,
  } = props;

  const isMobile = useIsMobile();
  const { profile } = useSession();
  const { mutateAsync: reportNoShow, isLoading } = useReportNoShow();

  const [isConfirmed, setIsConfirmed] = useState(false);
  const [reasonDescription, setReasonDescription] = useState("");
  const [replacementSelection, setReplacementSelection] = useState<boolean | null>(null);

  const {
    step,
    warningType,
    tripData,
    handleWarningConfirm,
    onWarningBack,
    onWarningClose,
    resetWarning,
  } = useNoShowWarning({
    workplaceId,
    shiftId,
    workerId,
    shiftStart,
    enabled: modalState.modalIsOpen && isDefined(workplaceId),
  });

  useEffect(() => {
    if (modalState.modalIsOpen) {
      resetWarning();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalState.modalIsOpen]);

  const handleCancel = () => {
    onWarningClose();
    setIsConfirmed(false);
    setReasonDescription("");
    setReplacementSelection(null);
    onCancel();
  };

  function handleWarningBack() {
    onWarningBack();
    onCancel();
  }

  const handleSubmit = async () => {
    if (!isConfirmed || !reasonDescription.trim()) {
      return;
    }

    if (canRequestReplacement && replacementSelection === null) {
      showErrorToast("Please select if you'd like us to post an urgent replacement shift.");
      return;
    }

    try {
      await reportNoShow({
        shiftId,
        workerId,
        userId: profile?.userId ?? "",
        reasonDescription: reasonDescription.trim(),
        requestReplacement: replacementSelection === true,
      });

      setIsConfirmed(false);
      setReasonDescription("");
      setReplacementSelection(null);
      onSuccess();
    } catch {
      showErrorToast("Failed to report No Show. Please try again.");
    }
  };

  const isSubmitDisabled =
    !isConfirmed ||
    !reasonDescription.trim() ||
    (canRequestReplacement === true && replacementSelection === null);

  const warningContent =
    isDefined(warningType) &&
    isDefined(tripData) &&
    isDefined(tripData.currentEtaDistance) &&
    isDefined(tripData.currentEtaDuration) ? (
      <Box
        sx={(theme: Theme) => ({
          padding: theme.spacing(isMobile ? 5 : 0),
          overflowY: "auto",
        })}
      >
        <NoShowWarningContent
          warningType={warningType}
          distanceInM={tripData.currentEtaDistance}
          etaDurationInMin={tripData.currentEtaDuration}
          lastLocationReceivedByServerAt={tripData.lastLocationReceivedByServerAt}
          trackingUrl={tripData.trackingUrl}
        />
      </Box>
    ) : null;

  const formContent = (
    <Box
      sx={(theme: Theme) => ({
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(4),
      })}
    >
      {isDefined(geofenceEntryTime) && (
        <GeofenceWarningBanner
          variant="entered-building"
          geofenceEntryTime={geofenceEntryTime}
          timezone={timezone}
        />
      )}

      <NoShowFormContent
        canRequestReplacement={canRequestReplacement}
        isConfirmed={isConfirmed}
        reasonDescription={reasonDescription}
        replacementSelection={replacementSelection}
        onConfirmedChange={setIsConfirmed}
        onReasonDescriptionChange={setReasonDescription}
        onReplacementSelectionChange={setReplacementSelection}
      />
    </Box>
  );

  const warningFooterContent = (
    <DialogFooter
      layout="horizontal"
      primaryAction={{
        label: "Confirm No Show",
        onClick: handleWarningConfirm,
      }}
      secondaryAction={{
        label: "Back",
        onClick: handleWarningBack,
      }}
    />
  );

  const formFooterContent = (
    <DialogFooter
      layout="horizontal"
      primaryAction={{
        label: "Confirm No Show",
        onClick: handleSubmit,
        isLoading,
        disabled: isSubmitDisabled,
      }}
      secondaryAction={{
        label: "Cancel",
        onClick: handleCancel,
      }}
    />
  );

  const content = step === "warning" ? warningContent : formContent;
  const footerContent = step === "warning" ? warningFooterContent : formFooterContent;
  const title = "Confirm No Show";

  if (isMobile) {
    return (
      <BottomSheet modalState={modalState} dismissible={false}>
        <DialogHeader title={title} onClose={handleCancel} />
        <Box
          sx={(theme: Theme) => ({
            padding: theme.spacing(5),
            overflowY: "auto",
            flex: 1,
          })}
        >
          {content}
        </Box>
        <Box
          sx={(theme: Theme) => ({
            borderTop: `1px solid ${theme.palette.border?.base}`,
          })}
        >
          {footerContent}
        </Box>
      </BottomSheet>
    );
  }

  return (
    <Dialog
      footerContent={footerContent}
      modalState={modalState}
      title={title}
      width={500}
      onClose={handleCancel}
    >
      {content}
    </Dialog>
  );
}
